import dynamic from 'next/dynamic';

import { prefetchModule } from '@/utils/prefetch-module';

import type { SignInModalProps } from './sign-in-modal';
import { SignInModalSkeleton } from './sign-in-modal-skeleton';

const loadSignInModal = () => import('./sign-in-modal');

// `error ? null` is mandatory, same as in lazy-sign-up-modal: on a post-deploy chunk 404 the fallback
// would stay mounted forever, and it is a `fixed inset-0` overlay with no close button and no Escape
// handler (Escape lives inside the real modal), so it would trap the page. Returning null degrades to
// "nothing opens and the page stays usable".
//
// No `pastDelay` gate on purpose: the click that mounts this has no other feedback, so the panel outline
// has to land on the first frame rather than after the loadable's 200ms delay.
const SignInModalWithSkeleton = dynamic(loadSignInModal, { loading: ({ error }) => (error ? null : <SignInModalSkeleton />) });

/**
 * The sign-in modal, loaded on demand.
 *
 * The header used to import the real modal statically because the Login button lived inside it, above the
 * fold. That put @headlessui/react, yup, react-hook-form, @hookform/resolvers and both captcha providers —
 * ~122 KB raw / ~40 KB gzip — in the first-load JS of every page that renders a header, to display a
 * dialog that is closed. The button now belongs to the header itself (plain markup, server-rendered, so
 * nothing about the above-the-fold paint changed) and this is what its click mounts.
 *
 * Every call site drives it the way the two pre-existing programmatic ones already did:
 * `classNameButton='hidden' forceOpenModal` plus `onModalStateChange` to learn when it closed.
 *
 * Wraps `animateOnMount={false}` rather than leaving it to each call site: the invariant is "a skeleton
 * painted this panel, so the panel must not fade in", and it belongs to the lazy path itself, not to
 * whoever happens to render it. Any static importer keeps the default and its entrance fade.
 */
export function LazySignInModal(props: Omit<SignInModalProps, 'animateOnMount'>) {
  return <SignInModalWithSkeleton {...props} animateOnMount={false} />;
}

/** Warms the sign-in chunk from a gesture that precedes the click (pointer enter, keyboard focus). */
export function prefetchSignInModal(): void {
  prefetchModule(loadSignInModal);
}
